import React, { useState, useEffect, ChangeEvent, FormEvent } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Textarea,
  Button,
  SimpleGrid,
  VStack,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";

interface Colindancia {
  id?: string;
  idClaveCatastral: string;
  nombre: string;
  primerApellido: string;
  segundoApellido: string;
  orientacion: string;
  descripcion: string;
  longitud?: number;
}

interface ColindanciasFormProps {
  idClaveCatastral: string; // Identificador para relacionar colindancias
}

const ColindanciasForm: React.FC<ColindanciasFormProps> = ({ idClaveCatastral }) => {
  const [colindancias, setColindancias] = useState<Colindancia[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null); // Índice de la colindancia en edición
  const [newColindancia, setNewColindancia] = useState<Colindancia>({
    idClaveCatastral,
    nombre: "",
    primerApellido: "",
    segundoApellido: "",
    orientacion: "",
    descripcion: "",
    longitud: undefined,
  });

  const toast = useToast();

  // Cargar colindancias existentes
  useEffect(() => {
    axios
      .get(`http://52.91.130.91:5000/api/colindanciasRoutes/${idClaveCatastral}`)
      .then((response) => setColindancias(response.data))
      .catch(() =>
        toast({
          title: "Error",
          description: "No se pudo cargar la información de las colindancias.",
          status: "error",
          duration: 5000,
          isClosable: true,
        })
      );
  }, [idClaveCatastral, toast]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewColindancia((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (value: string, name: string) => {
    setNewColindancia((prev) => ({ ...prev, [name]: parseFloat(value) || undefined }));
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setNewColindancia(colindancias[index]);
  };

  const handleDelete = async (id?: string) => {
    if (id) {
      try {
        await axios.delete(`http://52.91.130.91:5000/api/colindanciasRoutes/${id}`);
        setColindancias((prev) => prev.filter((colindancia) => colindancia.id !== id));
        toast({
          title: "Éxito",
          description: "Colindancia eliminada correctamente.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch {
        toast({
          title: "Error",
          description: "No se pudo eliminar la colindancia.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editingIndex !== null) {
        // Actualizar colindancia existente
        const updatedColindancia = await axios.put(
          `http://52.91.130.91:5000/api/colindanciasRoutes/${newColindancia.id}`,
          newColindancia
        );
        setColindancias((prev) => {
          const updated = [...prev];
          updated[editingIndex] = updatedColindancia.data;
          return updated;
        });
        toast({
          title: "Éxito",
          description: "Colindancia actualizada correctamente.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Insertar nueva colindancia
        const createdColindancia = await axios.post("http://52.91.130.91:5000/api/colindanciasRoutes", newColindancia);
        setColindancias((prev) => [...prev, createdColindancia.data]);
        toast({
          title: "Éxito",
          description: "Colindancia creada correctamente.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
      setNewColindancia({
        idClaveCatastral,
        nombre: "",
        primerApellido: "",
        segundoApellido: "",
        orientacion: "",
        descripcion: "",
        longitud: undefined,
      });
      setEditingIndex(null);
    } catch {
      toast({
        title: "Error",
        description: "No se pudo guardar la colindancia.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box p={[2, 4, 6]} maxW="1200px" mx="auto">
      <form onSubmit={handleSubmit}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          <FormControl>
            <FormLabel>Nombre</FormLabel>
            <Input
              name="nombre"
              value={newColindancia.nombre}
              onChange={handleChange}
              placeholder="Nombre"
              isRequired
            />
          </FormControl>

          <FormControl>
            <FormLabel>Primer Apellido</FormLabel>
            <Input
              name="primerApellido"
              value={newColindancia.primerApellido}
              onChange={handleChange}
              placeholder="Primer Apellido"
              isRequired
            />
          </FormControl>

          <FormControl>
            <FormLabel>Segundo Apellido</FormLabel>
            <Input
              name="segundoApellido"
              value={newColindancia.segundoApellido}
              onChange={handleChange}
              placeholder="Segundo Apellido"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Orientación</FormLabel>
            <Input
              name="orientacion"
              value={newColindancia.orientacion}
              onChange={handleChange}
              placeholder="Orientación"
              isRequired
            />
          </FormControl>

          <FormControl>
            <FormLabel>Descripción</FormLabel>
            <Textarea
              name="descripcion"
              value={newColindancia.descripcion}
              onChange={handleChange}
              placeholder="Descripción"
              isRequired
            />
          </FormControl>

          <FormControl>
            <FormLabel>Longitud</FormLabel>
            <NumberInput
              value={newColindancia.longitud || ""}
              onChange={(value) => handleNumberChange(value, "longitud")}
            >
              <NumberInputField name="longitud" />
            </NumberInput>
          </FormControl>
        </SimpleGrid>

        <Button type="submit" colorScheme="blue">
          {editingIndex !== null ? "Actualizar Colindancia" : "Agregar Colindancia"}
        </Button>
      </form>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        {colindancias.map((colindancia, index) => (
          <Box key={colindancia.id || index} p={4} borderWidth="1px" borderRadius="md">
            <strong>{colindancia.nombre}</strong> {colindancia.primerApellido}{" "}
            {colindancia.segundoApellido}
            <p>Orientación: {colindancia.orientacion}</p>
            <p>Descripción: {colindancia.descripcion}</p>
            <p>Longitud: {colindancia.longitud}</p>
            <Button
              size="sm"
              colorScheme="green"
              onClick={() => handleEdit(index)}
              mr={2}
            >
              Editar
            </Button>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => handleDelete(colindancia.id)}
            >
              Eliminar
            </Button>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
};

export default ColindanciasForm;
